/**
 * MatterLink — connect a research session to a matter.
 *
 * This is not a filter or a label: a linked matter's documents and extracted
 * context join the grounding set on EVERY query in the session, so the control
 * says what it changes and is unlinkable at any point.
 *
 * It renders inline in the sources & settings panel rather than as a modal —
 * the panel is already a side surface, and stacking a dialog over it to pick
 * one row is heavier than the choice deserves.
 *
 * A matter can be chosen BEFORE the first message. That is the backend's
 * preferred flow: the uuid rides on the chat-create (and first-query) payload
 * and the relation is made with the chat row, so the very first answer is
 * already grounded in the matter.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  PiBriefcaseLight,
  PiCaretDownLight,
  PiXLight,
} from 'react-icons/pi';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import { fetchMatters } from '../../store/actions/Matters/MatterActions';
import SearchInput from '../shared/TailwindComponents/SearchInput';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import useDebouncedValue from './useDebouncedValue';
import { ResearchLinkedMatter } from '../../store/actions/ChatAction';

/** Stable identity so the selector never yields a fresh array for "not loaded". */
const EMPTY_MATTERS: any[] = [];

interface Props {
  matter: ResearchLinkedMatter | null;
  isLoading?: boolean;
  isSaving?: boolean;
  /** No chat open yet — the link is persisted per chat group. */
  disabled?: boolean;
  onLink: (matterUuid: string, matter?: ResearchLinkedMatter) => void;
  onUnlink: () => void;
}

const MatterLink: React.FC<Props> = ({
  matter,
  isLoading = false,
  isSaving = false,
  disabled = false,
  onLink,
  onUnlink,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const matters = useAppSelector(
    (s: RootState) => (s.matters?.matters as any[] | undefined) ?? EMPTY_MATTERS
  );

  const [isPicking, setIsPicking] = useState(false);
  const [query, setQuery] = useState('');
  // The field stays instant; the filter waits for typing to settle.
  const settledQuery = useDebouncedValue(query, 150);
  const [isFetching, setIsFetching] = useState(false);
  const didFetch = useRef(false);

  // Fetch once, on first open — not on mount. Most sessions never link a
  // matter, and this is a multi-page fetch.
  useEffect(() => {
    if (!isPicking || didFetch.current) return;
    didFetch.current = true;
    let cancelled = false;
    setIsFetching(true);
    (async () => {
      try {
        const PAGE_SIZE = 100;
        const first: any = await dispatch(
          fetchMatters({ page: 1, page_size: PAGE_SIZE, sortBy: 'created_at', sortOrder: 'desc' })
        );
        const total = Number(first?.total_matters ?? 0);
        const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
        for (let page = 2; page <= pages; page++) {
          await dispatch(
            fetchMatters({ page, page_size: PAGE_SIZE, sortBy: 'created_at', sortOrder: 'desc' })
          );
        }
      } catch {
        // Surfaced by the global error toast already.
      } finally {
        // Not gated on `cancelled` — a StrictMode cleanup would otherwise
        // leave the list stuck on its skeleton forever.
        if (!cancelled) setIsFetching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isPicking, dispatch]);

  const visible = useMemo(() => {
    const needle = settledQuery.trim().toLowerCase();
    return matters
      .filter((m: any) => m?.uuid)
      .filter((m: any) => {
        if (!needle) return true;
        return [m.title, m.matter_number, m.jurisdiction, m.status_name]
          .some((field: any) => String(field || '').toLowerCase().includes(needle));
      })
      .slice(0, 50);
  }, [matters, settledQuery]);

  /* ── Linked ─────────────────────────────────────────────────── */

  if (matter) {
    return (
      <div data-testid="matter-link" data-linked="true">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted mb-1">
          {t('Matter')}
        </p>
        <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg
                        bg-surface-sunken/70 border border-hairline-soft">
          <PiBriefcaseLight className="w-4 h-4 mt-0.5 text-champagne flex-shrink-0" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-fg-strong truncate"
               data-testid="linked-matter-title">
              {matter.title || t('Linked matter')}
            </p>
            {matter.matter_number && (
              <p className="text-[10px] text-fg-muted truncate">{matter.matter_number}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onUnlink}
            disabled={isSaving}
            data-testid="matter-unlink"
            aria-label={t('Unlink matter')}
            title={t('Unlink matter')}
            className="p-1 rounded-md text-fg-muted hover:text-fg-strong
                       disabled:opacity-50 transition-colors duration-200 ease-slide"
          >
            <PiXLight className="w-3.5 h-3.5" />
          </button>
        </div>
        <p className="mt-1 text-[10px] text-fg-muted">
          {t('This matter’s documents are searched alongside your sources.')}
        </p>
      </div>
    );
  }

  /* ── Unlinked ───────────────────────────────────────────────── */

  return (
    <div data-testid="matter-link" data-linked="false">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted mb-1">
        {t('Matter')}
      </p>

      {isLoading ? (
        <Skeleton variant="list" rows={1} />
      ) : (
        <button
          type="button"
          onClick={() => setIsPicking((open) => !open)}
          disabled={disabled || isSaving}
          data-testid="matter-connect"
          className={clsx(
            'inline-flex items-center justify-between gap-1 w-full px-2.5 py-1.5 rounded-lg text-xs',
            'bg-surface-sunken text-fg-body border border-hairline-soft',
            'hover:border-champagne/50 transition-colors duration-200 ease-slide',
            (disabled || isSaving) && 'opacity-50 cursor-not-allowed'
          )}
        >
          <span className="inline-flex items-center gap-1.5 truncate">
            <PiBriefcaseLight className="w-3.5 h-3.5 text-fg-muted" aria-hidden />
            {t('Connect a matter')}
          </span>
          <PiCaretDownLight
            className={clsx(
              'w-3 h-3 text-fg-muted flex-shrink-0 transition-transform duration-200 ease-slide',
              isPicking && 'rotate-180'
            )}
            aria-hidden
          />
        </button>
      )}

      {isPicking && !disabled && (
        <div className="mt-2 space-y-2" data-testid="matter-picker">
          <SearchInput
            size="small"
            value={query}
            onChange={setQuery}
            placeholder={t('Search matters')}
            aria-label={t('Search matters')}
            data-testid="matter-search"
          />

          {isFetching ? (
            <Skeleton variant="list" rows={4} />
          ) : visible.length === 0 ? (
            <p className="py-3 text-center text-[11px] text-fg-muted" data-testid="matter-empty">
              {matters.length === 0
                ? t('No matters yet.')
                : t('No matches')}
            </p>
          ) : (
            <ul className="max-h-56 overflow-y-auto space-y-1">
              {visible.map((option: any) => (
                <li key={option.uuid}>
                  <button
                    type="button"
                    onClick={() => {
                      onLink(option.uuid, {
                        uuid: option.uuid,
                        id: option.id,
                        title: option.title,
                        matter_number: option.matter_number,
                      });
                      setIsPicking(false);
                      setQuery('');
                    }}
                    disabled={isSaving}
                    data-testid="matter-option"
                    data-uuid={option.uuid}
                    className="w-full text-left px-2 py-1.5 rounded-md
                               hover:bg-surface-sunken text-fg-body
                               disabled:opacity-50 transition-colors duration-200 ease-slide"
                  >
                    <span className="block text-xs font-medium text-fg-strong truncate">
                      {option.title || t('Untitled matter')}
                    </span>
                    {(option.matter_number || option.status_name) && (
                      <span className="block text-[10px] text-fg-muted truncate">
                        {[option.matter_number, option.status_name].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <p className="mt-1 text-[10px] text-fg-muted">
        {t('Ground answers in a matter’s own documents.')}
      </p>
    </div>
  );
};

export default MatterLink;
